import { ActivityEntityName } from '../entities/shared/activity.entity.name';
import { AuditDetails, Entity } from '../entities/entity';

export enum AIProcessInboxStatus {
    Pending = 'pending',
    Processing = 'processing',
    Completed = 'completed',
    Failed = 'failed',
    Cancelled = 'cancelled'
}

export type AIProcessInboxReviewStatus = 'pending' | 'approved' | 'declined';

export interface AIProcessInboxMetadataInfo {
    job?: Entity;
    stage?: Entity;
    bill?: Entity;
    task?: Entity;
}

/**
 * Payload of the AI extraction socket events (`extraction:processing` / `extraction:completed`).
 * Carries only the minimum needed to identify which inbox row changed — consumers re-fetch
 * the inbox list to derive any counts/summary, so they cannot drift from the row-level state.
 */
export interface AIExtractionEventPayload {
    inboxId: string;
    status: AIProcessInboxStatus;
}

export interface AIProcessInbox extends AuditDetails {
    _id: string;
    type: string;
    entity: ActivityEntityName;
    inputData: any;
    outputData: any;
    status: AIProcessInboxStatus;

    /** @description Workflow run ID for cancellation / reset support */
    workflowRunId?: string;

    /** @description Outbox record id when the process was handed to the AI service through the durable AI jobs queue */
    outboxId?: string;
    /** Task check identity; a late queue completion cannot overwrite a newer check on the same task. */
    validationAttemptId?: string;

    metadata?: AIProcessInboxMetadataInfo;

    /** @description Number of retry attempts */
    retryCount?: number;

    /** @description Last error message from failed workflow */
    lastError?: string;

    /** @description Neutral explanation when a durable result was reconciled after an earlier terminal timeout */
    completionNote?: string;

    /** @description Display title for the inbox record (vendor name, file name, or batch info) */
    title?: string;

    /** @description Batch identifier for grouping related inbox records from same upload */
    groupingId?: string;

    /** @description Stable attachment-derived identity for one bill group within a batch */
    groupKey?: string;

    /**
     * @description When a queued bill upload's images were split into groups. Saving a group deletes its
     * record, so this is the only lasting sign that grouping happened; a whole-upload retry is refused once set.
     */
    groupedAt?: Date;

    /** @description Human feedback on AI output quality */
    feedback?: AIProcessInboxFeedback;

    /** @description OTel trace ID from Mastra workflow execution, used for BrainTrust feedback */
    traceId?: string;

    /**
     * @description Root span ID of the Mastra workflow run.
     * Required to attach human feedback to the trace via `mastra.observability.addFeedback`
     * — without it, addFeedback tries to look up the span via `storage.observability.getTrace`,
     * which Mastra's MongoDB observability adapter does not implement (silently returns null
     * and skips the FeedbackEvent emit). See jms-ai/src/mastra/routes/feedback.route.ts.
     */
    rootSpanId?: string;

    /** @description Human review status for items requiring attention (e.g., failed validations) */
    reviewStatus?: AIProcessInboxReviewStatus;
}

export type AIFeedbackValue = 'positive' | 'negative';

export interface AIFeedbackSubmission {
    value: AIFeedbackValue;
    reason?: string;
}

export interface AIThreadFeedbackResponse {
    success: boolean;
    flagged?: boolean;
    reason?: string;
}

export interface AIProcessInboxFeedback {
    value: AIFeedbackValue;
    reason?: string;
    submittedAt: Date;
    submittedBy: string;
}
